import { useHistory } from "react-router-dom"
import * as Sentry from "@sentry/browser"

import { useAssertValidRootQuery, CurrentUser } from "graphql/smart-queries/user"
import { FLOWS, useCurrentFlow } from "hooks/useCurrentFlow"

import Analytics from "shared/Analytics"
import { routeBasenames } from "routes"

import { StartJourneyUserFragment, DrugTypeEnum, NextActionEnum, Journey } from "typings/types"

/**
 * As of this writing, users should only have one journey on Cove. NB we will
 * need to revise this once we support multiple journeys.
 *
 * @param user a user object
 * @param nextActionFilter Optional. if present, only return the journey if the specified next action is possible
 * @return the current active journey or null if there is no active journey
 */
export const maybeGetActiveJourney = (
  user: Maybe<CurrentUser | StartJourneyUserFragment>,
  nextActionFilter?: NextActionEnum
) => {
  if (!user) return null
  const { activeJourneys } = user

  if (!activeJourneys.length) return null

  if (activeJourneys.length > 1) {
    Sentry.withScope(scope => {
      scope.setExtra("activeJourneys", activeJourneys)

      Sentry.captureMessage("Unexpected: too many journeys.")
    })

    return null
  }

  /** TODO clean up casting */
  const journey = activeJourneys[0] as Journey

  if (nextActionFilter) return journey.nextActions.includes(nextActionFilter) ? journey : null

  return journey
}

export const assertActiveJourney = (user?: CurrentUser | StartJourneyUserFragment) => {
  const journey = maybeGetActiveJourney(user)

  if (!journey) {
    throw new Error("Unexpected: no journey found.")
  }

  return journey
}

/** Calculates the redirect path between V2/V3 flows (if the user is on the incorrect flow)
 *
 * @returns {string | null} path we want to redirect the user to or null if we shouldn't redirect
 */
export const useRedirectV2V3User = (): string | null => {
  const history = useHistory()
  const { userId, flow: desiredFlow } = useCurrentFlow()
  const { pathname } = history.location

  /* We assert that the query in useCurrentFlow is already loaded because we have a loading guard in pages/index.tsx */

  const currentFlow = pathname.indexOf(routeBasenames.purchase) === 0 ? FLOWS.V3 : FLOWS.V2

  const regex = new RegExp(`^(${FLOWS.V2.url}|${FLOWS.V3.url})`)
  const desiredPath: string = history.location.pathname.replace(regex, "")

  if (desiredFlow && currentFlow.version !== desiredFlow.version) {
    Analytics.onUserVersionFunneled({ id: userId }, desiredFlow.version)

    return desiredFlow.url + desiredPath
  }

  return null
}

export const useHasNurtec = () => {
  const { user } = useAssertValidRootQuery()
  const activeJourney = assertActiveJourney(user)

  const hasNurtec = !!activeJourney.userProducts.find(({ drugType }) => drugType === DrugTypeEnum.Rimegepant)

  return hasNurtec
}
